import { createRef, KeyboardEvent, MouseEvent, useEffect } from 'react';
import {
	getDomIndex,
	getNextFocusableElement,
} from '../../../../../../_helpers/dom';
import { getKeys } from '../../../../../../_helpers/keyboard';
import { HeaderProfileData } from '../../../../../../components/blackrock/advisor-center/_types/header';
import MyProfileAnchor from './components/my-profile-anchor/MyProfileAnchor.tsx';

import './HeaderMyProfile.scss';

export type HeaderMyProfileProps = {
	isProfileOpened: boolean;
	profile: HeaderProfileData;
	setProfileOpened: (state: boolean) => void;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const HeaderMyProfile: React.FC<HeaderMyProfileProps> = ({
	isProfileOpened,
	profile,
	setProfileOpened,
}) => {
	const ellipsis = '…';
	const characterLimit = 22;
	let focusableElements: HTMLElement[] = [];

	const el = createRef<HTMLAnchorElement>();

	useEffect(() => {
		window.addEventListener('click', () => {
			setOpened2(false);
		});

		return () => {
			focusableElements = [];
		};
	}, []);

	const setOpened2 = (newValue: boolean): void => {
		setProfileOpened(newValue);
	};

	const onClick = (event: MouseEvent) => {
		const isSignedIn = !!profile.userName;
		const isOpened = isProfileOpened;

		if (isSignedIn) {
			event.preventDefault();
			setOpened2(!isOpened);
		}
	};

	const cutLongName = (name: string): string => {
		return name.length > characterLimit
			? name.slice(0, characterLimit) + ellipsis
			: name;
	};

	const onKeyDownButton = (event: KeyboardEvent): void => {
		const keys = getKeys(event.nativeEvent);

		if ((!isProfileOpened && (keys.isEnter || keys.isSpace)) || keys.isDown) {
			event.preventDefault();

			setOpened2(true);
			focusableElements[0]?.focus();
		}
	};

	const onKeyDownItem = (event: KeyboardEvent): void => {
		const keys = getKeys(event.nativeEvent);
		const target = event.target as HTMLElement;
		const targetIndex = getDomIndex(focusableElements, target);
		const targetIsFirst = targetIndex === 0;
		const targetIsLast = targetIndex === focusableElements.length - 1;

		if (keys.isDown) {
			event.preventDefault();
			const currentElement = event.target as HTMLElement;
			const nextElement = currentElement.nextSibling as HTMLElement;

			nextElement?.focus();
			return;
		}

		if (keys.isUp) {
			event.preventDefault();
			const currentElement = event.target as HTMLElement;
			const previousElement = currentElement.previousSibling as HTMLElement;

			previousElement?.focus();
			return;
		}

		if (targetIsFirst && (keys.isUp || keys.isShiftTab)) {
			event.preventDefault();
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			el.current.focus();
			setOpened2(false);
			parent.focus();
			return;
		}

		if (targetIsLast && keys.isTab) {
			event.preventDefault();
			setOpened2(false);
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			getNextFocusableElement(target).focus();
			return;
		}

		if (keys.isEscape) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			el.current.focus();
			setOpened2(false);
			return;
		}
	};

	const profileData = profile;
	const isOpened = isProfileOpened;
	const signIn = profileData?.signIn;
	const signOut = profileData?.signOut;
	const contactUs = profileData?.contactUs;
	const editProfile = profileData?.editProfile;
	const userName = profileData?.userName;
	const ariaLabel = isProfileOpened
		? profileData?.closeAriaLabel
		: profileData?.openAriaLabel;
	focusableElements = [];
	const isVisible =
		(editProfile || signIn || signOut || contactUs) && !!profile.userName;

	const commonAnchorProps = {
		isMyProfileOpened: isOpened,
		onKeyDownItem,
		focusableElements,
	};

	return (
		isVisible && (
			<div
				className="blk-ac-header-my-profile"
				onClick={(e) => e.stopPropagation()}
			>
				<a
					data-final-url
					aria-label={ariaLabel}
					role="button"
					aria-controls="my-profile-drawer"
					className="front-of-bg"
					onClick={onClick}
					onKeyDown={onKeyDownButton}
					tabIndex={0}
					ref={el}
					href={signIn?.href}
				>
					<img
						src={profileData.icon?.path}
						alt=""
						className="icon-user"
						data-final-url
					/>
					<div className="user-detail">
						<span className="user-name blk-ac-body-xs">
							{userName ? cutLongName(userName) : 'Me'}
						</span>
						<i className="fa fa-chevron-down icon-down-arrow" />
					</div>
				</a>

				{userName && (
					<div
						className={'drawer behind-bg ' + (isOpened ? 'is-opened' : '')}
						id="my-profile-drawer"
					>
						<div className="drawer-wrapper">
							<MyProfileAnchor {...commonAnchorProps} menuItem={signIn} />

							<MyProfileAnchor {...commonAnchorProps} menuItem={contactUs} />

							<MyProfileAnchor {...commonAnchorProps} menuItem={editProfile} />

							<MyProfileAnchor {...commonAnchorProps} menuItem={signOut} />
						</div>
					</div>
				)}
			</div>
		)
	);
};

export default HeaderMyProfile;
